import * as types from '../mutation-types';
import AuditEventsAPI from '../../api/auditEvents';
import { throwErrorMessage } from 'dashboard/store/utils/api';

/**
 * Store for the audit_events endpoint.
 *
 * Keyset pagination, so this is "load more" rather than numbered pages: the API returns a
 * `next_cursor` and no total count. That is deliberate — a COUNT over a partitioned audit
 * table on every page load is what made the previous endpoint slow.
 */
const state = {
  records: [],
  cursor: null,
  hasMore: false,
  filterOptions: {
    action_categories: [],
    actions: [],
    sources: [],
    actor_types: [],
  },
  uiFlags: {
    fetchingList: false,
    fetchingMore: false,
    fetchingFilters: false,
    exporting: false,
    downloading: false,
  },
};

const getters = {
  getAuditEvents(_state) {
    return _state.records;
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getHasMore(_state) {
    return _state.hasMore;
  },
  getFilterOptions(_state) {
    return _state.filterOptions;
  },
};

const actions = {
  async fetch({ commit }, filters = {}) {
    commit(types.default.SET_AUDIT_EVENTS_UI_FLAG, { fetchingList: true });
    try {
      const response = await AuditEventsAPI.get(filters);
      const { audit_events: events = [], next_cursor: nextCursor } =
        response.data;
      commit(types.default.SET_AUDIT_EVENTS, events);
      commit(types.default.SET_AUDIT_EVENTS_CURSOR, nextCursor);
      return events;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.default.SET_AUDIT_EVENTS_UI_FLAG, { fetchingList: false });
    }
  },

  /** Appends the next page. Pass the same filters used for the initial fetch. */
  async fetchMore({ commit, state: _state }, filters = {}) {
    if (!_state.cursor) return [];

    commit(types.default.SET_AUDIT_EVENTS_UI_FLAG, { fetchingMore: true });
    try {
      const response = await AuditEventsAPI.get({
        ...filters,
        cursor: _state.cursor,
      });
      const { audit_events: events = [], next_cursor: nextCursor } =
        response.data;
      commit(types.default.APPEND_AUDIT_EVENTS, events);
      commit(types.default.SET_AUDIT_EVENTS_CURSOR, nextCursor);
      return events;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.default.SET_AUDIT_EVENTS_UI_FLAG, { fetchingMore: false });
    }
  },

  /** Fire-and-forget: the job emails a download link when the file is ready. */
  async requestExport({ commit }, { formatType, ...filters } = {}) {
    commit(types.default.SET_AUDIT_EVENTS_UI_FLAG, { exporting: true });
    try {
      await AuditEventsAPI.export({ formatType, ...filters });
      return true;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.default.SET_AUDIT_EVENTS_UI_FLAG, { exporting: false });
    }
  },

  /**
   * TEMPORARY / TESTING ONLY — fetches the file synchronously and returns the blob + a
   * filename, for the "Download now (test)" button. The real export path is requestExport
   * above (async job + emailed link); this bypasses it to test ExportService's output directly.
   */
  async downloadNow({ commit }, { formatType, ...filters } = {}) {
    commit(types.default.SET_AUDIT_EVENTS_UI_FLAG, { downloading: true });
    try {
      const response = await AuditEventsAPI.download({
        formatType,
        ...filters,
      });
      return { blob: response.data, headers: response.headers };
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.default.SET_AUDIT_EVENTS_UI_FLAG, { downloading: false });
    }
  },

  async fetchFilterOptions({ commit }) {
    commit(types.default.SET_AUDIT_EVENTS_UI_FLAG, { fetchingFilters: true });
    try {
      const response = await AuditEventsAPI.filters();
      commit(types.default.SET_AUDIT_EVENTS_FILTER_OPTIONS, response.data);
      return response.data;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.default.SET_AUDIT_EVENTS_UI_FLAG, {
        fetchingFilters: false,
      });
    }
  },
};

const mutations = {
  [types.default.SET_AUDIT_EVENTS_UI_FLAG](_state, data) {
    // Mutate in place to preserve reactivity, matching the auditTrail module.
    Object.assign(_state.uiFlags, data);
  },

  [types.default.SET_AUDIT_EVENTS](_state, events) {
    _state.records = events;
  },

  [types.default.APPEND_AUDIT_EVENTS](_state, events) {
    _state.records = [..._state.records, ...events];
  },

  [types.default.SET_AUDIT_EVENTS_CURSOR](_state, cursor) {
    _state.cursor = cursor || null;
    _state.hasMore = Boolean(cursor);
  },

  [types.default.SET_AUDIT_EVENTS_FILTER_OPTIONS](_state, options) {
    _state.filterOptions = { ..._state.filterOptions, ...options };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
